import { Fragment, type KeyboardEvent } from "react";
import {
  alpha,
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, Timeline, WhatsApp } from "@mui/icons-material";
import {
  statsTableCellTextSx,
  statsTableHeadCellSx,
} from "src-next/modules/clinical-insights/helpers/insightPatientsTableStyles";
import { InsightPatientExpandedPanel } from "./InsightPatientExpandedPanel";
import {
  INSIGHT_PANEL_COMPACT,
  INSIGHT_PANEL_NARROW,
  READABLE_BODY_SX,
  TABLE_ROW_PY,
  analysisBg,
  insightSituationChipSx,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import { buildPatientSituationChip, patientHasPhone } from "./insightPatientsUtils";
import type { InsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import type { InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

interface InsightPatientsTableProps {
  pageRows: InsightPatientRow[];
  statusLabelByKey: Record<string, string>;
  texts: InsightAnalysisTexts;
  selectedIds: Set<string>;
  expandedId: string | null;
  accentColor: string;
  isProfessional: boolean;
  whatsAppDisabled: boolean;
  whatsAppTooltip: string;
  allPageSelected: boolean;
  somePageSelected: boolean;
  onToggleSelectAllPage: () => void;
  onToggleSelect: (patientId: string) => void;
  onEvolve: (row: InsightPatientRow) => void;
  onWhatsApp: (row: InsightPatientRow) => void;
  onToggleExpand: (patientId: string) => void;
  /** Default: botão Evoluir visível para profissional. */
  showEvolve?: boolean;
}

/** Overrides locais — evita minWidth desktop do pacote admin. */
const insightPatientsTableSx = {
  width: "100%",
  minWidth: 0,
  tableLayout: "fixed",
} as const;

const tableLayoutSx = {
  ...insightPatientsTableSx,
  "& col.insight-col-check": { width: 44 },
  "& col.insight-col-patient": { width: "auto" },
  "& col.insight-col-situation": { width: 172 },
  "& col.insight-col-actions": { width: 96 },
  [INSIGHT_PANEL_NARROW]: {
    "& col.insight-col-patient": { width: "auto" },
    "& col.insight-col-actions": { width: 88 },
  },
} as const;

const hideSituationColumnSx = {
  "& .insight-col-situation": {
    [INSIGHT_PANEL_NARROW]: { display: "none" },
  },
} as const;

const tableModeSx = {
  display: "block",
  [INSIGHT_PANEL_COMPACT]: { display: "none" },
} as const;

const compactListModeSx = {
  display: "none",
  flexDirection: "column",
  gap: 1,
  width: "100%",
  minWidth: 0,
  [INSIGHT_PANEL_COMPACT]: { display: "flex" },
} as const;

const inlineSituationSx = {
  display: "none",
  maxWidth: "100%",
  [INSIGHT_PANEL_NARROW]: { display: "block" },
  [INSIGHT_PANEL_COMPACT]: { display: "none" },
} as const;

interface SituationChipProps {
  situation: ReturnType<typeof buildPatientSituationChip>;
  accentColor: string;
}

function PatientSituationChip({ situation, accentColor }: SituationChipProps) {
  return (
    <Tooltip title={situation.tooltip} arrow placement="top">
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%", minWidth: 0 }}>
        <Typography
          component="span"
          title={situation.chipLabel}
          sx={insightSituationChipSx(accentColor, false)}
        >
          {situation.chipLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
}

interface PatientActionsProps {
  row: InsightPatientRow;
  isProfessional: boolean;
  rowWhatsAppDisabled: boolean;
  rowWhatsAppTitle: string;
  onEvolve: (row: InsightPatientRow) => void;
  onWhatsApp: (row: InsightPatientRow) => void;
  showEvolve?: boolean;
  iconSize?: number;
  buttonPadding?: number;
}

function PatientActions({
  row,
  isProfessional,
  rowWhatsAppDisabled,
  rowWhatsAppTitle,
  onEvolve,
  onWhatsApp,
  showEvolve = true,
  iconSize = 26,
  buttonPadding = 1.1,
}: PatientActionsProps) {
  return (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
      {isProfessional && showEvolve ? (
        <Tooltip title="Evoluir">
          <IconButton
            size="medium"
            color="secondary"
            onClick={() => onEvolve(row)}
            sx={{ p: buttonPadding }}
          >
            <Timeline sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title={rowWhatsAppTitle}>
        <span>
          <IconButton
            size="medium"
            color="success"
            disabled={rowWhatsAppDisabled}
            onClick={() => onWhatsApp(row)}
            sx={{ p: buttonPadding }}
          >
            <WhatsApp sx={{ fontSize: iconSize }} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

interface ExpandChevronProps {
  expanded: boolean;
  accentColor: string;
}

function ExpandChevron({ expanded, accentColor }: ExpandChevronProps) {
  return expanded ? (
    <ExpandLess sx={{ fontSize: 20, color: alpha(accentColor, 0.75) }} />
  ) : (
    <ExpandMore sx={{ fontSize: 20, color: "action.active" }} />
  );
}

const patientNameSx = {
  ...statsTableCellTextSx,
  ...READABLE_BODY_SX,
  fontWeight: 600,
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [INSIGHT_PANEL_NARROW]: {
    whiteSpace: "normal",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },
} as const;

export function InsightPatientsTable({
  pageRows,
  statusLabelByKey,
  texts,
  selectedIds,
  expandedId,
  accentColor,
  isProfessional,
  whatsAppDisabled,
  whatsAppTooltip,
  allPageSelected,
  somePageSelected,
  onToggleSelectAllPage,
  onToggleSelect,
  onEvolve,
  onWhatsApp,
  onToggleExpand,
  showEvolve = true,
}: InsightPatientsTableProps) {
  const renderExpandedBlock = (row: InsightPatientRow, expanded: boolean) => (
    <Collapse in={expanded} timeout={220} unmountOnExit>
      <InsightPatientExpandedPanel
        items={row.items}
        accentColor={accentColor}
        itemColumnLabel={texts.itemColumnLabel}
        emptyRowText={texts.emptyPatientItemText}
      />
    </Collapse>
  );

  const handleCompactRowKeyDown = (event: KeyboardEvent, patientId: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggleExpand(patientId);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        className="insight-patients-table-full"
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          overflowX: "hidden",
          overflowY: "visible",
          ...tableModeSx,
          ...hideSituationColumnSx,
        }}
      >
        <Table size="small" sx={tableLayoutSx}>
          <colgroup>
            <col className="insight-col-check" />
            <col className="insight-col-patient" />
            <col className="insight-col-situation" />
            <col className="insight-col-actions" />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={statsTableHeadCellSx}>
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={!allPageSelected && somePageSelected}
                  onChange={onToggleSelectAllPage}
                />
              </TableCell>
              <TableCell sx={statsTableHeadCellSx} className="insight-col-patient">
                Paciente
              </TableCell>
              <TableCell sx={statsTableHeadCellSx} className="insight-col-situation">
                Situação
              </TableCell>
              <TableCell sx={statsTableHeadCellSx} className="insight-col-actions">
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => {
              const expanded = expandedId === row.patientId;
              const selected = selectedIds.has(row.patientId);
              const situation = buildPatientSituationChip(
                row.items,
                statusLabelByKey,
                texts.noItemsTooltip,
                texts.itemPlural
              );
              const rowPhoneOk = patientHasPhone(row.patient);
              const rowWhatsAppDisabled = whatsAppDisabled || !rowPhoneOk;
              const rowWhatsAppTitle = !rowPhoneOk
                ? "Paciente sem telefone cadastrado"
                : whatsAppTooltip;

              return (
                <Fragment key={row.patientId}>
                  <TableRow
                    hover
                    selected={selected}
                    onClick={() => onToggleExpand(row.patientId)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: expanded ? alpha(accentColor, 0.03) : undefined,
                      "&.Mui-selected": { bgcolor: analysisBg(accentColor, 0.06) },
                      "& .MuiTableCell-root": { py: TABLE_ROW_PY },
                    }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        onChange={() => onToggleSelect(row.patientId)}
                      />
                    </TableCell>
                    <TableCell
                      className="insight-col-patient"
                      sx={{ minWidth: 0, maxWidth: 0, verticalAlign: "middle" }}
                    >
                      <Stack spacing={0.5} sx={{ minWidth: 0, width: "100%" }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ minWidth: 0, width: "100%" }}
                        >
                          <Box sx={{ flexShrink: 0, display: "flex" }}>
                            <ExpandChevron expanded={expanded} accentColor={accentColor} />
                          </Box>
                          <Typography title={row.patientName} sx={patientNameSx}>
                            {row.patientName}
                          </Typography>
                        </Stack>
                        <Box className="insight-patient-situation-inline" sx={inlineSituationSx}>
                          <PatientSituationChip situation={situation} accentColor={accentColor} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell
                      className="insight-col-situation"
                      sx={{ minWidth: 0, verticalAlign: "middle", px: { xs: 0.5, sm: 1 } }}
                    >
                      <PatientSituationChip situation={situation} accentColor={accentColor} />
                    </TableCell>
                    <TableCell
                      className="insight-col-actions"
                      sx={{ px: 0.5, verticalAlign: "middle" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PatientActions
                        row={row}
                        isProfessional={isProfessional}
                        rowWhatsAppDisabled={rowWhatsAppDisabled}
                        rowWhatsAppTitle={rowWhatsAppTitle}
                        onEvolve={onEvolve}
                        onWhatsApp={onWhatsApp}
                        showEvolve={showEvolve}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        py: 0,
                        px: { xs: 0.5, sm: 1 },
                        borderBottom: expanded ? undefined : 0,
                        bgcolor: expanded ? alpha(accentColor, 0.02) : undefined,
                      }}
                    >
                      {renderExpandedBlock(row, expanded)}
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack className="insight-patients-list-compact" sx={compactListModeSx}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 0.25,
            pb: 0.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Checkbox
            size="small"
            checked={allPageSelected}
            indeterminate={!allPageSelected && somePageSelected}
            onChange={onToggleSelectAllPage}
            inputProps={{ "aria-label": "Selecionar todos da página" }}
          />
          <Typography sx={{ ...READABLE_BODY_SX, fontWeight: 700 }}>Paciente</Typography>
        </Stack>

        {pageRows.map((row) => {
          const expanded = expandedId === row.patientId;
          const selected = selectedIds.has(row.patientId);
          const situation = buildPatientSituationChip(
            row.items,
            statusLabelByKey,
            texts.noItemsTooltip,
            texts.itemPlural
          );
          const rowPhoneOk = patientHasPhone(row.patient);
          const rowWhatsAppDisabled = whatsAppDisabled || !rowPhoneOk;
          const rowWhatsAppTitle = !rowPhoneOk
            ? "Paciente sem telefone cadastrado"
            : whatsAppTooltip;

          return (
            <Box
              key={row.patientId}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              onClick={() => onToggleExpand(row.patientId)}
              onKeyDown={(e) => handleCompactRowKeyDown(e, row.patientId)}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: 2,
                border: "1px solid",
                borderColor: selected ? alpha(accentColor, 0.35) : "divider",
                bgcolor: expanded
                  ? alpha(accentColor, 0.04)
                  : selected
                    ? analysisBg(accentColor, 0.06)
                    : "background.paper",
                px: 1,
                py: 1.1,
                cursor: "pointer",
                outline: "none",
                "&:focus-visible": {
                  boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
                },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ minWidth: 0 }}>
                <Box onClick={(e) => e.stopPropagation()} sx={{ pt: 0.15 }}>
                  <Checkbox
                    size="small"
                    checked={selected}
                    onChange={() => onToggleSelect(row.patientId)}
                    inputProps={{ "aria-label": `Selecionar ${row.patientName}` }}
                  />
                </Box>
                <Stack spacing={0.65} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                    <Box sx={{ flexShrink: 0, display: "flex" }}>
                      <ExpandChevron expanded={expanded} accentColor={accentColor} />
                    </Box>
                    <Typography title={row.patientName} sx={{ ...patientNameSx, whiteSpace: "normal" }}>
                      {row.patientName}
                    </Typography>
                  </Stack>
                  <PatientSituationChip situation={situation} accentColor={accentColor} />
                </Stack>
                <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, ml: -0.5 }}>
                  <PatientActions
                    row={row}
                    isProfessional={isProfessional}
                    rowWhatsAppDisabled={rowWhatsAppDisabled}
                    rowWhatsAppTitle={rowWhatsAppTitle}
                    onEvolve={onEvolve}
                    onWhatsApp={onWhatsApp}
                    showEvolve={showEvolve}
                    iconSize={24}
                    buttonPadding={0.85}
                  />
                </Box>
              </Stack>
              {expanded ? (
                <Box sx={{ pt: 1, pl: 4.5, pr: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  {renderExpandedBlock(row, expanded)}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
